import { z } from 'zod';
import { BankAccountSchema, MoneySchema, PaginationQuerySchema, WithdrawalIdSchema, WithdrawalStatusSchema, paginatedResponseSchema, } from './common.js';
import { CreditSettlementSchema } from './verification.js';
/**
 * `POST /v1/withdrawals`, `GET /v1/withdrawals`, `POST /v1/withdrawals/{id}/cancel` (SAD §8, M-01).
 */
/** Path parameters for the withdrawal-scoped endpoints. */
export const WithdrawalIdParamSchema = z
    .object({
    id: WithdrawalIdSchema,
})
    .strict();
// ---------------------------------------------------------------------------------------------
// POST /v1/withdrawals — create a withdrawal (enters the pool)
// ---------------------------------------------------------------------------------------------
/**
 * Request body for `POST /v1/withdrawals` (SAD §8).
 *
 * The website has already locked the withdrawer's balance and mints `lock_reference` before
 * calling this — PF will not enqueue an unlocked withdrawal (422 `LOCK_REFERENCE_REQUIRED`).
 */
export const CreateWithdrawalRequestSchema = z
    .object({
    client_reference: z.string().min(1).max(255),
    amount: MoneySchema,
    withdrawer: z
        .object({
        participant: z.string().min(1).max(128),
        account_ref: z.string().min(1).max(255),
        /**
         * The website's *display* handle for this customer — same field, same semantics and the
         * same `account_directory` destination as the reserve request's
         * `depositor.account_display`; see that schema for the full note. Optional.
         */
        account_display: z.string().min(1).max(255).optional(),
    })
        .strict(),
    receiver_account: BankAccountSchema,
    /** REQUIRED — proof the funds are locked (SAD §8). Absence is a 422, never a silent default. */
    lock_reference: z.string().min(1).max(255),
})
    .strict();
/** `201` response for `POST /v1/withdrawals` — a newly-pooled, matchable withdrawal. */
export const CreateWithdrawalResponseSchema = z
    .object({
    id: WithdrawalIdSchema,
    status: z.literal('AVAILABLE'),
    lock_reference: z.string().min(1),
})
    .strict();
// ---------------------------------------------------------------------------------------------
// GET /v1/withdrawals — reconciliation feed
// ---------------------------------------------------------------------------------------------
/** Query parameters for `GET /v1/withdrawals` (SAD §8: keyset, `updated_since=`). */
export const WithdrawalReconQuerySchema = PaginationQuerySchema;
/**
 * One row of the reconciliation feed (SAD §8) — enough to reconcile a funds-lock ledger, and
 * never bank details the website does not own.
 *
 * `amount` and `origin_lock_reference` arrived with split fill (migration 0034). Once a
 * withdrawal can be split, this feed carries rows the website has never seen: a slice is born
 * inside the claim, carrying a derived `lock_reference` of `<parent>#<n>`, with no event the
 * website ever acted on. Without the amount it cannot size that slice against the single funds
 * lock it holds; without the origin reference it cannot find that lock at all — and treating the
 * derived reference as the parent's would settle a whole lock for a partial payment. Together
 * they are what lets a reconciler heal a slice it missed.
 *
 * Both are optional so an older website's parser keeps working; `origin_lock_reference` is absent
 * for a whole withdrawal, which is every withdrawal until a participant is split-enabled.
 */
export const WithdrawalReconItemSchema = z
    .object({
    id: WithdrawalIdSchema,
    status: WithdrawalStatusSchema,
    lock_reference: z.string().min(1),
    amount: MoneySchema.optional(),
    /** The *parent's* lock reference when this row is a slice. Absent for a whole withdrawal. */
    origin_lock_reference: z.string().min(1).optional(),
    /**
     * On a `MERGED` row these two are the fold instruction, not a carve one (see
     * `withdrawal.slice_merged`): `amount` is what moved out of this slice's lock and into
     * `origin_lock_reference`'s, and a reconciler must fold — never materialise, never release.
     * Both locks stay held across it; no member balance moves.
     *
     * A reconciler that materialises every row carrying `origin_lock_reference` regardless of
     * status would carve this slice a second time, which is why `MERGED` is gated behind
     * `capability_slice_merge`: no website is sent one before it can tell the two apart.
     */
    /**
     * FULFILLED rows only, and only when the paying credit carried an amount adjustment: how
     * this lock settles — the same object `withdrawal.fulfilled` carries. On a CROSS-website
     * match this is the recovery path's ONLY settle instruction (the lock's owner never sees
     * `transaction.confirmed`): a reconciler that finds a FULFILLED row with this present must
     * settle by it — UNDER: commit the short figure, release `released_amount`; OVER: commit in
     * full and debit up to `extra_debited_amount` — never commit the whole lock as if it were
     * absent. Additive and optional; absent means the whole lock committed.
     */
    settlement: CreditSettlementSchema.optional(),
})
    .strict();
/** `200` response for `GET /v1/withdrawals`. */
export const WithdrawalReconResponseSchema = paginatedResponseSchema(WithdrawalReconItemSchema);
// ---------------------------------------------------------------------------------------------
// POST /v1/withdrawals/{id}/cancel
// ---------------------------------------------------------------------------------------------
/** `POST /v1/withdrawals/{id}/cancel` takes no body — AVAILABLE only; 409 otherwise (SAD §8). */
export const CancelWithdrawalRequestSchema = z.object({}).strict();
/** `200` response for `POST /v1/withdrawals/{id}/cancel`. */
export const CancelWithdrawalResponseSchema = z
    .object({
    id: WithdrawalIdSchema,
    status: z.literal('CANCELLED'),
    lock_reference: z.string().min(1),
    /**
     * The cancelled withdrawal's CURRENT amount — for a parent that was split (ซอยยอด) this is
     * the post-shrink residual, not the amount at creation. It is the authoritative figure the
     * website's inline release must be measured against: a website that releases its own stored
     * figure instead silently over-refunds whenever a slice never materialized on its side, and
     * the `withdrawal.released` webhook that follows finds the lock already RELEASED and leaves
     * no trail.
     *
     * Naming note: on the terminal webhooks `amount` means "a slice's share of a lock filed
     * elsewhere" and a parent's figure travels as `residual_amount` — that split exists because
     * one webhook key must unambiguously mark a slice. This response has no such ambiguity: the
     * payload describes exactly the row named in the path, so the row's current amount is plainly
     * `amount`. Do not port the webhook's `amount ?? residual_amount` precedence here.
     */
    amount: MoneySchema,
})
    .strict();
//# sourceMappingURL=withdrawals.js.map